import { useState, useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { SearchableSelect } from '@/components/SearchableSelect';
import { toast } from 'sonner';
import api from '@/lib/api';
import { X, Pencil, Calendar, Clock, AlarmClock, Plus, Check, ChevronRight, MessageSquare, ArrowLeft, Share2 } from 'lucide-react';
import ShareDialog from '@/components/ShareDialog';

const STATUS_LABELS = { new: 'New', in_progress: 'In Progress', on_hold: 'On Hold', cancelled: 'Cancelled', completed: 'Completed', overdue: 'Overdue' };
const STATUS_COLORS = { new: 'bg-amber-500', in_progress: 'bg-blue-500', on_hold: 'bg-orange-500', cancelled: 'bg-red-500', completed: 'bg-emerald-500', overdue: 'bg-rose-600' };
const PRIORITY_OPTS = [{value:'high',label:'High'},{value:'medium',label:'Medium'},{value:'low',label:'Low'},{value:'',label:'None'}];
const STATUS_OPTS = Object.entries(STATUS_LABELS).map(([v,l]) => ({value:v,label:l}));

export default function TaskDetail({ task, projects, onClose, onRefresh }) {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [activityForm, setActivityForm] = useState({ description: '', next_followup: '' });
  const [showRemarks, setShowRemarks] = useState(false);
  const [remarksText, setRemarksText] = useState('');
  const [pendingStatus, setPendingStatus] = useState('');
  const [viewingSubtask, setViewingSubtask] = useState(null);
  const [shareOpen, setShareOpen] = useState(false);
  const [shareSubtask, setShareSubtask] = useState(null);

  const projectMap = {};
  projects.forEach(p => { projectMap[p.id] = p.name; });

  useEffect(() => {
    if (task) setForm({
      title: task.title, description: task.description || '', priority: task.priority,
      status: task.status, project_id: task.project_id,
      tags: (task.tags || []).join(', '), color_tags: task.color_tags || [],
      start_date: task.start_date || '', due_date: task.due_date || '',
      subtasks: (task.subtasks || []).map(st => ({ ...st })), remarks: task.remarks || ''
    });
  }, [task]);

  useEffect(() => {
    if (viewingSubtask && task) {
      const updated = (task.subtasks || []).find(s => s.id === viewingSubtask.id);
      if (updated) setViewingSubtask(updated);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [task]);

  // 'n' to open Add Subtask inside an open task detail (captures before global 'new task')
  useEffect(() => {
    if (viewingSubtask) return; // subtask has its own 'n' handling
    const handleN = (e) => {
      if (e.key !== 'n' || e.ctrlKey || e.metaKey || e.altKey) return;
      const isTyping = ['INPUT','TEXTAREA'].includes(e.target.tagName) || e.target.getAttribute('role') === 'combobox';
      if (isTyping) return;
      e.preventDefault();
      e.stopImmediatePropagation();
      const btn = document.querySelector('[data-testid="add-subtask-btn"]');
      if (btn) btn.click();
    };
    window.addEventListener('keydown', handleN, true);
    return () => window.removeEventListener('keydown', handleN, true);
  }, [viewingSubtask]);

  // Escape: edit → cancel, then close task detail
  useEffect(() => {
    const handleKey = (e) => {
      if (e.key !== 'Escape') return;
      if (viewingSubtask) return; // subtask handles its own escape
      // Check if any popover/dialog is open
      const openPopover = document.querySelector('[data-radix-popper-content-wrapper]');
      if (openPopover) return;
      const openDialog = document.querySelector('[role="dialog"]');
      if (openDialog && showRemarks) { setShowRemarks(false); e.stopImmediatePropagation(); return; }
      if (editing) { setEditing(false); e.stopImmediatePropagation(); return; }
      onClose();
      e.stopImmediatePropagation();
    };
    window.addEventListener('keydown', handleKey, true); // capture phase
    return () => window.removeEventListener('keydown', handleKey, true);
  }, [editing, onClose, viewingSubtask, showRemarks]);

  const saveField = async (fields) => {
    try { await api.put(`/tasks/${task.id}`, fields); toast.success('Updated'); onRefresh(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  const saveFullEdit = async () => {
    try {
      const payload = { ...form, tags: form.tags ? form.tags.split(',').map(t => t.trim()).filter(Boolean) : [], start_date: form.start_date || null, due_date: form.due_date || null };
      if ((payload.status === 'on_hold' || payload.status === 'cancelled') && payload.status !== task.status) {
        setPendingStatus(payload.status); setRemarksText(form.remarks || ''); setShowRemarks(true); return;
      }
      await api.put(`/tasks/${task.id}`, payload); toast.success('Task updated'); setEditing(false); onRefresh();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  const confirmStatusWithRemarks = async () => {
    if (!remarksText.trim()) { toast.error('Remarks required'); return; }
    try { await api.put(`/tasks/${task.id}`, { status: pendingStatus, remarks: remarksText }); toast.success('Status updated'); setShowRemarks(false); onRefresh(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  const addActivity = async () => {
    if (!activityForm.description) { toast.error('Description required'); return; }
    try { await api.post(`/tasks/${task.id}/activity`, activityForm); toast.success('Activity added'); setActivityForm({ description: '', next_followup: '' }); onRefresh(); }
    catch { toast.error('Error'); }
  };

  const toggleSubtask = async (subtaskId, current) => {
    const updated = (task.subtasks || []).map(s => s.id === subtaskId ? { ...s, is_completed: !current, status: !current ? 'completed' : 'new' } : s);
    saveField({ subtasks: updated });
  };

  const addNewSubtask = async (title) => {
    const newSub = {
      id: (crypto?.randomUUID?.() || Math.random().toString(36).slice(2)),
      title,
      is_completed: false,
      status: 'new',
      priority: 'medium',
      description: '',
      start_date: null,
      end_date: null,
      activity_log: [],
    };
    const updated = [...(task.subtasks || []), newSub];
    try { await api.put(`/tasks/${task.id}`, { subtasks: updated }); toast.success('Subtask added'); onRefresh(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  if (!task) return null;

  // ─── Subtask full-screen ───
  if (viewingSubtask) {
    return <SubtaskDetail task={task} subtask={viewingSubtask} projects={projects}
      onClose={() => setViewingSubtask(null)} onRefresh={onRefresh} onCloseAll={onClose}
      onNavigateToTask={() => setViewingSubtask(null)} />;
  }

  const activityLog = (task.activity_log || []).slice().reverse();

  return (
    <div className="fixed inset-0 z-[60] bg-white dark:bg-zinc-950 overflow-hidden flex flex-col" data-testid="task-detail">
      {/* Breadcrumb bar */}
      <div className="flex items-center justify-between h-12 px-4 border-b border-zinc-200 dark:border-zinc-800 shrink-0">
        <div className="flex items-center gap-1.5 text-sm min-w-0">
          <button onClick={onClose} className="text-zinc-400 hover:text-zinc-700 truncate" data-testid="breadcrumb-workspace">{projectMap[task.project_id] || 'Workspace'}</button>
          <span className="text-zinc-300">/</span>
          <span className="text-zinc-900 dark:text-zinc-100 font-medium truncate">{task.title}</span>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {!editing && <Button variant="outline" size="sm" onClick={() => setShareOpen(true)} data-testid="share-task-btn"><Share2 className="h-3.5 w-3.5 mr-1" /> Share</Button>}
          {!editing && <Button variant="outline" size="sm" onClick={() => setEditing(true)} data-testid="edit-task-detail-btn"><Pencil className="h-3.5 w-3.5 mr-1" /> Edit</Button>}
          {editing && <Button size="sm" onClick={saveFullEdit} data-testid="save-task-detail-btn"><Check className="h-3.5 w-3.5 mr-1" /> Save</Button>}
          {editing && <Button variant="ghost" size="sm" onClick={() => setEditing(false)}>Cancel</Button>}
          <Button variant="ghost" size="icon" onClick={onClose} data-testid="close-task-detail"><X className="h-4 w-4" /></Button>
        </div>
      </div>

      <div className="flex-1 flex flex-col md:flex-row overflow-hidden">
        <div className="flex-1 overflow-y-auto p-4 md:p-6 md:border-r border-zinc-200 dark:border-zinc-800">
          {editing ? <Input value={form.title} onChange={e => setForm(p => ({...p, title: e.target.value}))} className="text-2xl font-semibold h-auto py-2 mb-4" />
            : <h1 className="text-2xl font-semibold tracking-tight mb-4">{task.title}</h1>}

          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
            <FieldDisplay label="Status" editing={editing}
              view={<Badge className={`${STATUS_COLORS[task.status]} text-white text-xs px-2 py-0.5`}>{STATUS_LABELS[task.status]}</Badge>}
              edit={<SearchableSelect value={form.status} onValueChange={v => setForm(p => ({...p, status: v}))} options={STATUS_OPTS} placeholder="Status" />} />
            <FieldDisplay label="Priority" editing={editing}
              view={<p className="text-sm capitalize">{task.priority}</p>}
              edit={<SearchableSelect value={form.priority} onValueChange={v => setForm(p => ({...p, priority: v}))} options={PRIORITY_OPTS} placeholder="Priority" />} />
            <FieldDisplay label="Workspace" editing={false} view={<p className="text-sm">{projectMap[task.project_id] || '—'}</p>} />
            <FieldDisplay label="Start Date" editing={editing}
              view={<p className="text-sm">{task.start_date || '—'}</p>}
              edit={<Input type="date" value={form.start_date} onChange={e => setForm(p => ({...p, start_date: e.target.value}))} />} />
            <FieldDisplay label="Due Date" editing={editing}
              view={<p className="text-sm">{task.due_date || '—'}</p>}
              edit={<Input type="date" value={form.due_date} onChange={e => setForm(p => ({...p, due_date: e.target.value}))} />} />
            <FieldDisplay label="Tags" editing={editing}
              view={<div className="flex gap-1 flex-wrap">{(task.tags||[]).map(t => <Badge key={t} variant="secondary" className="text-[10px]">{t}</Badge>)}{(task.color_tags||[]).map(c => <span key={c} className="w-4 h-4 rounded-full" style={{backgroundColor:c}} />)}{!(task.tags?.length) && !(task.color_tags?.length) && <span className="text-sm text-zinc-400">—</span>}</div>}
              edit={<Input value={form.tags} onChange={e => setForm(p => ({...p, tags: e.target.value}))} placeholder="tag1, tag2" />} />
          </div>

          {task.remarks && <div className="mb-6 p-3 bg-amber-50 dark:bg-amber-950 rounded-lg border border-amber-200 dark:border-amber-800"><Label className="text-[11px] text-amber-600 uppercase tracking-wider">Remarks</Label><p className="text-sm mt-1">{task.remarks}</p></div>}

          <div className="mb-6">
            <Label className="text-[11px] text-zinc-400 uppercase tracking-wider">Description</Label>
            {editing ? <Textarea value={form.description} onChange={e => setForm(p => ({...p, description: e.target.value}))} placeholder="Add description..." className="mt-1" rows={3} />
              : <p className="text-sm mt-1 whitespace-pre-wrap">{task.description || <span className="text-zinc-400">No description</span>}</p>}
          </div>

          {/* Subtasks table */}
          <SubtaskTable subtasks={task.subtasks || []} onClickSubtask={setViewingSubtask} onToggle={toggleSubtask} onAdd={addNewSubtask} />
        </div>

        <ActivityPanel activityLog={activityLog} activityForm={activityForm} setActivityForm={setActivityForm} onAdd={addActivity} />
      </div>

      <Dialog open={showRemarks} onOpenChange={setShowRemarks}>
        <DialogContent data-testid="remarks-dialog"><DialogHeader><DialogTitle>Remarks Required</DialogTitle><DialogDescription>Reason for {STATUS_LABELS[pendingStatus]}</DialogDescription></DialogHeader>
          <Textarea value={remarksText} onChange={e => setRemarksText(e.target.value)} placeholder="Enter remarks..." rows={3} />
          <DialogFooter><Button variant="outline" onClick={() => setShowRemarks(false)}>Cancel</Button><Button onClick={confirmStatusWithRemarks}>Confirm</Button></DialogFooter>
        </DialogContent>
      </Dialog>
      <ShareDialog open={shareOpen} onClose={() => setShareOpen(false)} recordType="task" recordId={task.id} recordName={task.title} />
    </div>
  );
}

// ─── Subtask Full-Screen Detail ───
function SubtaskDetail({ task, subtask, projects, onClose, onRefresh, onCloseAll, onNavigateToTask }) {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [activityForm, setActivityForm] = useState({ description: '', next_followup: '' });
  const [shareOpen, setShareOpen] = useState(false);

  const projectMap = {};
  projects.forEach(p => { projectMap[p.id] = p.name; });

  useEffect(() => {
    if (subtask) setForm({
      title: subtask.title, status: subtask.status || 'new', priority: subtask.priority || '',
      start_date: subtask.start_date || '', end_date: subtask.end_date || '',
      tags: (subtask.tags || []).join(', ')
    });
  }, [subtask]);

  // Escape: edit → cancel, then back to task
  useEffect(() => {
    const handleKey = (e) => {
      if (e.key !== 'Escape') return;
      const openPopover = document.querySelector('[data-radix-popper-content-wrapper]');
      if (openPopover) return;
      if (editing) { setEditing(false); e.stopImmediatePropagation(); return; }
      onClose(); // back to task
      e.stopImmediatePropagation();
    };
    window.addEventListener('keydown', handleKey, true); // capture phase
    return () => window.removeEventListener('keydown', handleKey, true);
  }, [editing, onClose]);

  const saveSubtask = async () => {
    const updated = (task.subtasks || []).map(s => s.id === subtask.id ? {
      ...s, title: form.title, status: form.status, priority: form.priority,
      start_date: form.start_date || null, end_date: form.end_date || null,
      tags: form.tags ? form.tags.split(',').map(t => t.trim()).filter(Boolean) : [],
    } : s);
    try { await api.put(`/tasks/${task.id}`, { subtasks: updated }); toast.success('Subtask updated'); setEditing(false); onRefresh(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  const addActivity = async () => {
    if (!activityForm.description) { toast.error('Description required'); return; }
    try { await api.post(`/tasks/${task.id}/subtasks/${subtask.id}/activity`, activityForm); toast.success('Activity added'); setActivityForm({ description: '', next_followup: '' }); onRefresh(); }
    catch { toast.error('Error'); }
  };

  if (!subtask) return null;
  const activityLog = (subtask.activity_log || []).slice().reverse();

  return (
    <div className="fixed inset-0 z-[60] bg-white dark:bg-zinc-950 overflow-hidden flex flex-col" data-testid="subtask-detail">
      {/* Breadcrumb bar - each segment clickable */}
      <div className="flex items-center justify-between h-12 px-4 border-b border-zinc-200 dark:border-zinc-800 shrink-0">
        <div className="flex items-center gap-1.5 text-sm min-w-0">
          <button onClick={onClose} className="p-1 hover:bg-zinc-100 dark:hover:bg-zinc-800 rounded shrink-0"><ArrowLeft className="h-4 w-4 text-zinc-400" /></button>
          <button onClick={onCloseAll} className="text-zinc-400 hover:text-zinc-700 truncate" data-testid="breadcrumb-ws">{projectMap[task.project_id]}</button>
          <span className="text-zinc-300">/</span>
          <button onClick={onNavigateToTask} className="text-zinc-400 hover:text-zinc-700 truncate" data-testid="breadcrumb-task">{task.title}</button>
          <span className="text-zinc-300">/</span>
          <span className="text-zinc-900 dark:text-zinc-100 font-medium truncate">{subtask.title}</span>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {!editing && <Button variant="outline" size="sm" onClick={() => setShareOpen(true)} data-testid="share-subtask-btn"><Share2 className="h-3.5 w-3.5 mr-1" /> Share</Button>}
          {!editing && <Button variant="outline" size="sm" onClick={() => setEditing(true)}><Pencil className="h-3.5 w-3.5 mr-1" /> Edit</Button>}
          {editing && <Button size="sm" onClick={saveSubtask}><Check className="h-3.5 w-3.5 mr-1" /> Save</Button>}
          {editing && <Button variant="ghost" size="sm" onClick={() => setEditing(false)}>Cancel</Button>}
          <Button variant="ghost" size="icon" onClick={onCloseAll}><X className="h-4 w-4" /></Button>
        </div>
      </div>

      <div className="flex-1 flex flex-col md:flex-row overflow-hidden">
        <div className="flex-1 overflow-y-auto p-4 md:p-6 md:border-r border-zinc-200 dark:border-zinc-800">
          <p className="text-xs text-zinc-400 mb-2">Subtask of <span className="font-medium text-zinc-600">{task.title}</span></p>
          {editing ? <Input value={form.title} onChange={e => setForm(p => ({...p, title: e.target.value}))} className="text-2xl font-semibold h-auto py-2 mb-4" />
            : <h1 className="text-2xl font-semibold tracking-tight mb-4">{subtask.title}</h1>}

          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
            <FieldDisplay label="Status" editing={editing}
              view={<Badge className={`${STATUS_COLORS[subtask.status] || 'bg-amber-500'} text-white text-xs px-2 py-0.5`}>{STATUS_LABELS[subtask.status] || 'New'}</Badge>}
              edit={<SearchableSelect value={form.status} onValueChange={v => setForm(p => ({...p, status: v}))} options={STATUS_OPTS} placeholder="Status" />} />
            <FieldDisplay label="Priority" editing={editing}
              view={<p className="text-sm capitalize">{subtask.priority || '—'}</p>}
              edit={<SearchableSelect value={form.priority} onValueChange={v => setForm(p => ({...p, priority: v}))} options={PRIORITY_OPTS} placeholder="Priority" />} />
            <FieldDisplay label="Workspace" editing={false} view={<p className="text-sm">{projectMap[task.project_id] || '—'}</p>} />
            <FieldDisplay label="Start Date" editing={editing}
              view={<p className="text-sm">{subtask.start_date || '—'}</p>}
              edit={<Input type="date" value={form.start_date} onChange={e => setForm(p => ({...p, start_date: e.target.value}))} />} />
            <FieldDisplay label="End Date" editing={editing}
              view={<p className="text-sm">{subtask.end_date || '—'}</p>}
              edit={<Input type="date" value={form.end_date} onChange={e => setForm(p => ({...p, end_date: e.target.value}))} />} />
            <FieldDisplay label="Tags" editing={editing}
              view={<div className="flex gap-1 flex-wrap">{(subtask.tags||[]).map(t => <Badge key={t} variant="secondary" className="text-[10px]">{t}</Badge>)}{!(subtask.tags?.length) && <span className="text-sm text-zinc-400">—</span>}</div>}
              edit={<Input value={form.tags} onChange={e => setForm(p => ({...p, tags: e.target.value}))} placeholder="tag1, tag2" />} />
          </div>

          {(subtask.checklist || []).length > 0 && (
            <div className="mb-6"><Label className="text-[11px] text-zinc-400 uppercase tracking-wider mb-2">Checklist</Label>
              <div className="space-y-1">{subtask.checklist.map((item, i) => (
                <div key={i} className="flex items-center gap-2 py-1"><Checkbox checked={item.done} className="h-3.5 w-3.5" /><span className={`text-sm ${item.done ? 'line-through text-zinc-400' : ''}`}>{item.text}</span></div>
              ))}</div></div>
          )}
        </div>

        <ActivityPanel activityLog={activityLog} activityForm={activityForm} setActivityForm={setActivityForm} onAdd={addActivity} />
      </div>
      <ShareDialog open={shareOpen} onClose={() => setShareOpen(false)} recordType="task_sub" recordId={subtask.id} recordName={subtask.title} parentId={task.id} subCollection="subtasks" />
    </div>
  );
}

// ─── Subtask Table ───
function SubtaskTable({ subtasks, onClickSubtask, onToggle, onAdd }) {
  const [newTitle, setNewTitle] = useState('');
  const [adding, setAdding] = useState(false);
  const inputRef = useRef(null);
  const submit = async (keepAdding = false) => {
    const val = newTitle.trim();
    if (!val) {
      setAdding(false);
      setNewTitle('');
      return;
    }
    await onAdd?.(val);
    setNewTitle('');
    if (keepAdding) {
      setTimeout(() => inputRef.current?.focus(), 50);
    } else {
      setAdding(false);
    }
  };
  return (
    <div className="mb-6">
      <div className="flex items-center justify-between mb-3">
        <Label className="text-[11px] text-zinc-400 uppercase tracking-wider">
          Subtasks <span className="text-zinc-500">{subtasks.filter(s=>!s.is_completed).length} open / {subtasks.length} total</span>
        </Label>
        {!adding && <Button size="sm" variant="ghost" onClick={() => setAdding(true)} data-testid="add-subtask-btn"><Plus className="h-3 w-3 mr-1" /> Add Subtask</Button>}
      </div>
      {adding && (
        <div className="flex items-center gap-2 mb-2">
          <Input ref={inputRef} value={newTitle} onChange={e => setNewTitle(e.target.value)}
            placeholder="Subtask title · Enter: save + add next · Empty Enter: done"
            className="flex-1"
            onKeyDown={e => {
              if (e.key === 'Enter') {
                e.preventDefault();
                // If empty → close (save + done); if has content → save + add new one
                submit(!!newTitle.trim());
              } else if (e.key === 'Escape') {
                setAdding(false); setNewTitle('');
              }
            }}
            autoFocus data-testid="new-subtask-input" />
          <Button size="sm" onClick={() => submit(false)} data-testid="save-new-subtask-btn"><Check className="h-3 w-3" /></Button>
          <Button size="sm" variant="ghost" onClick={() => { setAdding(false); setNewTitle(''); }}><X className="h-3 w-3" /></Button>
        </div>
      )}
      {subtasks.length === 0 && !adding && <p className="text-xs text-zinc-400 py-4 text-center border border-dashed border-zinc-200 dark:border-zinc-800 rounded-xl">No subtasks yet. Click "Add Subtask" to create one.</p>}
      {subtasks.length > 0 && (
      <div className="border border-zinc-200 dark:border-zinc-800 rounded-xl overflow-hidden">
        <div className="grid grid-cols-12 gap-1 px-3 py-2 bg-zinc-50 dark:bg-zinc-900/50 text-[10px] font-medium text-zinc-400 uppercase tracking-wider border-b border-zinc-200 dark:border-zinc-800">
          <div className="col-span-5">Name</div><div className="col-span-2">Status</div>
          <div className="col-span-2 hidden md:block">Dates</div><div className="col-span-2">Priority</div><div className="col-span-1"></div>
        </div>
        {subtasks.map(st => (
          <div key={st.id} className="grid grid-cols-12 gap-1 px-3 py-2.5 border-b border-zinc-100 dark:border-zinc-800 hover:bg-zinc-50 dark:hover:bg-zinc-900 cursor-pointer items-center group"
            onClick={() => onClickSubtask(st)} data-testid={`subtask-row-${st.id}`}>
            <div className="col-span-5 flex items-center gap-2 min-w-0" onClick={e => e.stopPropagation()}>
              <Checkbox checked={st.is_completed} onCheckedChange={() => onToggle(st.id, st.is_completed)} className="h-3.5 w-3.5 shrink-0" />
              <span className={`text-sm truncate ${st.is_completed ? 'line-through text-zinc-400' : ''}`}>{st.title}</span>
              {(st.activity_log||[]).length > 0 && <span className="text-[10px] text-zinc-400 shrink-0"><MessageSquare className="h-3 w-3 inline" /> {st.activity_log.length}</span>}
            </div>
            <div className="col-span-2">
              {st.status && st.status !== 'new' ? <Badge className={`text-[9px] px-1.5 py-0 ${STATUS_COLORS[st.status] || 'bg-zinc-300'} text-white`}>{STATUS_LABELS[st.status] || st.status}</Badge> : <span className="text-[10px] text-zinc-400">New</span>}
            </div>
            <div className="col-span-2 hidden md:block text-[10px] text-zinc-400">
              {st.start_date || st.end_date ? `${st.start_date || '?'} → ${st.end_date || '?'}` : '—'}
            </div>
            <div className="col-span-2 text-[10px] text-zinc-500 capitalize">{st.priority || '—'}</div>
            <div className="col-span-1 flex justify-end"><ChevronRight className="h-3.5 w-3.5 text-zinc-300 group-hover:text-zinc-500" /></div>
          </div>
        ))}
      </div>
      )}
    </div>
  );
}

// ─── Shared ───
function FieldDisplay({ label, editing, view, edit }) {
  return <div><Label className="text-[11px] text-zinc-400 uppercase tracking-wider">{label}</Label><div className="mt-1">{editing && edit ? edit : view}</div></div>;
}

function ActivityPanel({ activityLog, activityForm, setActivityForm, onAdd }) {
  return (
    <div className="w-full md:w-[380px] flex flex-col bg-zinc-50 dark:bg-zinc-900/50 overflow-hidden shrink-0">
      <div className="p-4 border-b border-zinc-200 dark:border-zinc-800"><h3 className="text-sm font-semibold">Activity</h3></div>
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {activityLog.length === 0 && <p className="text-sm text-zinc-400 text-center py-8">No activity yet</p>}
        {activityLog.map((entry, i) => (
          <div key={entry.id || i} className="flex gap-3">
            <div className="w-8 h-8 rounded-full bg-zinc-200 dark:bg-zinc-700 flex items-center justify-center text-xs font-medium shrink-0">{(entry.created_by_username || '?').charAt(0).toUpperCase()}</div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1"><span className="text-xs font-medium">{entry.created_by_username || 'User'}</span><span className="text-[10px] text-zinc-400">{new Date(entry.created_at).toLocaleString()}</span></div>
              <div className="bg-white dark:bg-zinc-800 rounded-xl p-3 border border-zinc-200 dark:border-zinc-700"><p className="text-sm whitespace-pre-wrap">{entry.description}</p></div>
              {entry.next_followup && <span className="text-[11px] text-amber-600 flex items-center gap-1 mt-1"><AlarmClock className="h-3 w-3" /> Follow-up: {new Date(entry.next_followup).toLocaleString()}</span>}
            </div>
          </div>
        ))}
      </div>
      <div className="p-4 border-t border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 space-y-2">
        <Textarea placeholder="Write a comment..." value={activityForm.description}
          onChange={e => setActivityForm(p => ({...p, description: e.target.value}))} className="min-h-[60px] text-sm"
          onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); onAdd(); } }} />
        <div className="flex items-center gap-2">
          <Input type="datetime-local" value={activityForm.next_followup} onChange={e => setActivityForm(p => ({...p, next_followup: e.target.value}))} className="flex-1 text-xs h-8" />
          <Button size="sm" onClick={onAdd}><Plus className="h-3 w-3 mr-1" /> Add</Button>
        </div>
      </div>
    </div>
  );
}
